import * as Sentry from '@sentry/capacitor';
import { Capacitor } from '@capacitor/core';

/**
 * Sentry Configuration
 *
 * Uses @sentry/capacitor SDK which automatically handles all platforms:
 * - Web: Uses browser SDK internally
 * - iOS/Android: Uses native SDKs for crash reporting and performance monitoring
 *
 * The Capacitor SDK wraps the appropriate platform-specific SDK automatically,
 * so we don't need to conditionally choose between @sentry/react and @sentry/capacitor.
 *
 * Environment variable (optional):
 * - VITE_ENVIRONMENT: 'development' | 'staging' | 'production' (defaults to 'production')
 *
 * Sampling rates (consistent across all environments):
 * - Traces: 10% of transactions
 * - Session Replays: 0% (disabled - using PostHog for session replay)
 * - Error Replays: 100% of errors
 *
 * Reference: https://docs.sentry.io/platforms/javascript/guides/capacitor/
 */

let isInitialized = false;

export function initializeSentry() {
  if (isInitialized) {
    return;
  }
  isInitialized = true;

  Sentry.init({
    dsn: 'https://ec313205c4cd4f67957ea5afd7ed157a@o190156.ingest.sentry.io/1468344',
    environment: import.meta.env.VITE_ENVIRONMENT || 'production',
    release: '1.0.0',

    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.replayIntegration({
        // Privacy configuration: unmask all content for full session replay visibility
        // Note: Ensure this complies with your privacy policy and user consent requirements
        maskAllText: false,
        blockAllMedia: false,
        maskAllInputs: false,
      }),
    ],

    tracePropagationTargets: ['localhost', /^https:\/\/.*\.investwithbloom\.com/, /^https:\/\/.*\.render\.com/],

    // Sampling rates (consistent across all environments)
    tracesSampleRate: 0.1, // 10% of transactions
    replaysSessionSampleRate: 0.0, // Disabled (using PostHog for session replay)
    replaysOnErrorSampleRate: 1.0, // 100% of errors

    // Error filtering
    beforeSend(event, hint) {
      const error = hint.originalException;

      // Ignore network offline errors (handled by offline toast)
      if (
        error instanceof Error &&
        (error.message?.includes('Network Error') ||
          error.message?.includes('internet connection appears to be offline'))
      ) {
        return null;
      }

      // Ignore Firebase Installations network failures (SDK internal, not actionable)
      // These occur when Firebase SDK tries to register installations or refresh tokens
      // during poor network conditions. Firebase has built-in retry logic.
      const isFailedFetch =
        (error instanceof TypeError || error?.name === 'TypeError') && error?.message?.includes('Failed to fetch');

      if (isFailedFetch) {
        const errorMessage = (error as Error).message || '';
        const stacktrace = event.exception?.values?.[0]?.stacktrace?.frames;
        const breadcrumbs = event.breadcrumbs || [];

        // Check stacktrace for Firebase Installations SDK (works with source maps)
        const hasFirebaseInstallationsInStack = stacktrace?.some(
          (frame) => frame.filename?.includes('installations/src/') || frame.module?.includes('installations/src/'),
        );

        // Check error message for Firebase domain (some errors include URL in message)
        const hasFirebaseInstallationsInMessage = errorMessage.includes('firebaseinstallations.googleapis.com');

        // Check breadcrumbs for Firebase Installations URL (most reliable for minified code)
        const hasFirebaseInstallationsInBreadcrumbs = breadcrumbs.some(
          (crumb) =>
            crumb.category === 'fetch' &&
            typeof crumb.data?.url === 'string' &&
            crumb.data.url.includes('firebaseinstallations.googleapis.com'),
        );

        if (
          hasFirebaseInstallationsInStack ||
          hasFirebaseInstallationsInMessage ||
          hasFirebaseInstallationsInBreadcrumbs
        ) {
          return null;
        }
      }

      // Ignore expected 401s (handled by auth flow)
      if ((error as any)?.response?.status === 401) {
        return null;
      }

      return event;
    },

    // Ignore errors from browser extensions and benign errors
    ignoreErrors: [
      'top.GLOBALS',
      'chrome-extension://',
      'moz-extension://',
      'ResizeObserver loop limit exceeded',
      'Non-Error promise rejection captured',
      // Firebase messaging unsupported browser (BLOOM-FRONTEND-WEB-X4)
      'messaging/unsupported-browser',
      // Firebase Installations offline errors (BLOOM-FRONTEND-WEB-XV, BLOOM-FRONTEND-WEB-XT, BLOOM-FRONTEND-WEB-Y6)
      // Firebase SDK rejects with app-offline when navigator.onLine is false; retries automatically
      'installations/app-offline',
      // Firebase Installations not-registered error: occurs when the app hasn't completed
      // the installation registration flow yet; Firebase retries automatically
      'installations/not-registered',
      // AbortError from cancelled fetch requests (BLOOM-FRONTEND-WEB-XF)
      // These occur when users navigate away or requests are intentionally cancelled
      'AbortError',
    ],

    denyUrls: [/extensions\//i, /^chrome:\/\//i, /^moz-extension:\/\//i],
  });

  // Set global tags
  Sentry.setTag('platform', Capacitor.getPlatform());
  Sentry.setTag('is_mobile', Capacitor.isNativePlatform() ? 'true' : 'false');

  // Note: Global error handlers (window.error and window.unhandledrejection)
  // are automatically handled by Sentry's default GlobalHandlers integration.
  // No need to add manual listeners to avoid double-capture.
}

// User context management
export function setUserContext(userId: string | null, email?: string, isSubscribed?: boolean) {
  if (!userId) {
    Sentry.setUser(null);
    return;
  }

  Sentry.setUser({
    id: userId,
    email: email || undefined,
  });

  if (isSubscribed !== undefined) {
    Sentry.setTag('subscription_status', isSubscribed ? 'active' : 'free');
  }
}

export function clearUserContext() {
  Sentry.setUser(null);
}

// Export Sentry for use throughout app
export { Sentry };
